import { useCallback } from 'react';
import { InMemoryCache, useApolloClient } from '@apollo/client';
import type {
  AssetMatch,
  VisualMatch,
} from '@frameio/graphql-client/generated';
import {
  AssetClipMatch,
  useAssetClipMatchFragment,
} from '@frameio/graphql-client/generated';

/**
 * Exposes helper functions for setting and getting asset clip matches.
 * This is a client-only hook that uses Apollo Client's cache to store and retrieve clip matches.
 * which are used to display clip matches in the media viewer.
 */
export const useGetAssetClipMatch = (assetId: string) => {
  return useAssetClipMatchFragment({
    from: {
      __typename: 'AssetMatch',
      id: assetId,
    },
  })?.data as AssetMatch;
};

export const useSetAssetClipMatch = () => {
  const { cache } = useApolloClient();

  return useCallback(
    (assetId: string, visualMatch: VisualMatch, activeClipIndex?: number) =>
      cache.writeFragment({
        id: cache.identify({
          __typename: 'AssetMatch',
          id: assetId,
        }),
        fragment: AssetClipMatch,
        fragmentName: 'AssetClipMatch',
        data: {
          __typename: 'AssetMatch',
          id: assetId,
          activeClipIndex: activeClipIndex ?? 0,
          visualMatch,
        },
        broadcast: true,
      }),
    [cache]
  );
};

export const useClearAssetClipMatch = () => {
  const { cache } = useApolloClient();
  return useCallback(
    (assetId?: string) => {
      if (assetId) {
        cache.evict({
          id: cache.identify({
            __typename: 'AssetMatch',
            id: assetId,
          }),
          broadcast: true,
        });
        return;
      }

      const extracted = cache.extract();
      const idsToEvict = Object.keys(extracted).filter((storeId) => {
        if (storeId.startsWith('AssetMatch:')) return true;
        const record = (extracted as Record<string, unknown>)[storeId] as
          | { __typename?: string }
          | undefined;
        return record?.__typename === 'AssetMatch';
      });

      if (idsToEvict.length === 0) return;

      if (cache instanceof InMemoryCache) {
        cache.batch({
          update(batchedCache) {
            idsToEvict.forEach((id) => {
              batchedCache.evict({ id, broadcast: false });
            });
          },
        });
      } else {
        idsToEvict.forEach((id) => {
          cache.evict({ id, broadcast: true });
        });
      }

      cache.gc();
    },
    [cache]
  );
};
